import React from 'react';

import { useMutationUpdateMobileAppSubmissionStatusHook } from '@fd/customHooks/useMutationUpdateMobileAppSubmissionStatusHook';
import { getMobileAppSubmissionDetailsQueryKey } from '@fd/customHooks/useQueryMobileAppsSubmissionDetailsHook';
import {
  App,
  MobileAppsSubmissionStatus,
  UpdateMobileAppsSubmissionStatus,
} from '@flipdish/api-client-typescript';
import { makeStyles, Theme } from '@material-ui/core';
import AndroidRoundedIcon from '@material-ui/icons/AndroidRounded';
import ArrowForwardIosOutlinedIcon from '@material-ui/icons/ArrowForwardIosOutlined';
import NewReleasesOutlinedIcon from '@material-ui/icons/NewReleasesOutlined';
import ShopOutlined from '@material-ui/icons/ShopOutlined';
import { Translate } from 'react-localize-redux';
import { useQueryClient } from 'react-query';
import Permissions from 'react-redux-permissions';
import { useLocation } from 'react-router';

import { Button, Typography } from '@fd/ui/atoms';
import { IconBanner } from '@fd/ui/atoms/IconBanner';
import LinkButton from '@fd/ui/Button/LinkButton';
import LinkIconButton from '@fd/ui/Button/LinkIconButton';
import { DescriptiveCard } from '@fd/ui/molecules/DescriptiveCard';

import { ReactComponent as AndroidIcon } from '../../../../../assets/images/android-head.svg';
import { ReactComponent as DashedLine } from '../../../../../assets/images/dashed-line.svg';
import { ReactComponent as FlipdishIcon } from '../../../../../assets/images/fdl.svg';
import { ReactComponent as SuccessOutlineIcon } from '../../../../../assets/images/success-check.svg';
import { InternalReviewControls } from './InternalReviewControls';

const useStyles = makeStyles((theme: Theme) => ({
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  checkSubmissionButton: {
    marginTop: theme.spacing(6),
  },
  completeStep: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
  },
  container: {
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
  },
  flipdishIcon: {
    height: '80px',
    width: '94px',
  },
  googlePlayStoreIcon: {
    color: theme.palette.primary.main,
    height: '63px',
    width: '67px',
  },
  incompleteStep: {
    color: theme.palette.grey[400],
    marginRight: theme.spacing(1),
  },
  publishedContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  published: {
    color: '#20c26e',
  },
  readyToSubmitIcon: {
    height: '25px',
    width: '44px',
    color: theme.palette.primary.main,
  },
  reviewProcessContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing(3),
  },
  successAndroidIcon: {
    color: '#20C26E',
    height: '80px',
    width: '94px',
  },
  successBanner: {
    backgroundColor: '#d8f2e3',
  },
  successButton: {
    marginTop: theme.spacing(5),
    backgroundColor: '#20C26E',
    '&:hover': {
      backgroundColor: '#248f42',
    },
  },
  successIcon: {
    marginLeft: theme.spacing(1),
    color: '#20C26E',
  },
  skeltonSpacing: {
    marginBottom: theme.spacing(3),
  },
  warningBanner: {
    backgroundColor: '#fdf2d8',
  },
  warningIcon: {
    color: '#FFBC2C',
    height: '70px',
    width: '73px',
  },
}));

export type PlatformSettingCardProps = {
  appId: string;
  isFlipdishStaff: boolean;
  loading?: boolean;
  onError: () => void;
  status: MobileAppsSubmissionStatus.UpdateStatusEnum | null;
  statusId?: number;
  submissionId?: number;
  type: MobileAppsSubmissionStatus.TypeEnum;
};

export const PlatformSettingCard = (props: PlatformSettingCardProps) => {
  const { appId, isFlipdishStaff, loading, onError, status, statusId, submissionId, type } = props;
  const location = useLocation();
  const classes = useStyles();
  const titleTranslationId =
    type === MobileAppsSubmissionStatus.TypeEnum.IOS ? 'iOS' : ('Android' as TranslationId);
  const queryClient = useQueryClient();

  const handleStatusUpdateSuccess = () => {
    //Improve this by updating query cache manually instead of calling api again
    queryClient.invalidateQueries(getMobileAppSubmissionDetailsQueryKey);
  };

  const handleStatusUpdateFailed = () => {
    onError();
  };

  const { mutate, isLoading } = useMutationUpdateMobileAppSubmissionStatusHook(
    appId,
    submissionId as number,
    handleStatusUpdateSuccess,
    handleStatusUpdateFailed
  );

  const handleFdReviewSubmit = (appType: MobileAppsSubmissionStatus.TypeEnum) => {
    const updatedStatus = {
      Type: appType,
      UpdateStatus: MobileAppsSubmissionStatus.UpdateStatusEnum.Submitted,
    };
    mutate(updatedStatus);
  };

  const handleInternalReviewReject = (appType: UpdateMobileAppsSubmissionStatus.TypeEnum) => {
    mutate({
      MobileAppsSubmissionStatusId: statusId,
      Type: appType,
      UpdateStatus: MobileAppsSubmissionStatus.UpdateStatusEnum.Rejected,
    });
  };

  const handleInternalReviewPass = (appType: MobileAppsSubmissionStatus.TypeEnum) => {
    const newStatus =
      status === MobileAppsSubmissionStatus.UpdateStatusEnum.Submitted
        ? MobileAppsSubmissionStatus.UpdateStatusEnum.InReview
        : MobileAppsSubmissionStatus.UpdateStatusEnum.Approved;
    mutate({
      MobileAppsSubmissionStatusId: statusId,
      Type: appType,
      UpdateStatus: newStatus,
    });
  };

  const getAndroidCardProps = () => {
    switch (status) {
      case MobileAppsSubmissionStatus.UpdateStatusEnum.None:
        return {
          banner: <IconBanner icon={<AndroidIcon className={classes.readyToSubmitIcon} />} />,
          description: 'Android_submit_for_review_description' as TranslationId,
        };
      case MobileAppsSubmissionStatus.UpdateStatusEnum.Submitted:
        return {
          banner: <IconBanner icon={<FlipdishIcon className={classes.flipdishIcon} />} />,
          description: 'Android_submitted_to_flipdish_description' as TranslationId,
          subtitle: 'Submitted_for_review' as TranslationId,
        };
      case MobileAppsSubmissionStatus.UpdateStatusEnum.InReview:
        return {
          banner: <IconBanner icon={<ShopOutlined className={classes.googlePlayStoreIcon} />} />,
          description: 'Android_submitted_to_google_description' as TranslationId,
          subtitle: 'Submitted_for_Google_review' as TranslationId,
        };
      case MobileAppsSubmissionStatus.UpdateStatusEnum.Approved:
        return {
          banner: (
            <IconBanner
              classeName={classes.successBanner}
              icon={<AndroidRoundedIcon className={classes.successAndroidIcon} />}
            />
          ),
          description: 'App_submission_success_description' as TranslationId,
        };
      case MobileAppsSubmissionStatus.UpdateStatusEnum.Rejected:
        return {
          banner: (
            <IconBanner
              classeName={classes.warningBanner}
              icon={<NewReleasesOutlinedIcon className={classes.warningIcon} />}
            />
          ),
          description: 'App_submission_rejected_description' as TranslationId,
        };
      default:
        return {
          banner: <IconBanner />,
          description: 'View_and_edit_your_android_settings' as TranslationId,
          subtitle: 'Manage_settings' as TranslationId,
        };
    }
  };

  const getIosCardProps = () => {
    switch (status) {
      default:
        return {
          banner: <IconBanner />,
          description: 'iOS_submit_for_review_description' as TranslationId,
        };
    }
  };

  const getCardProps = () => {
    return type === MobileAppsSubmissionStatus.TypeEnum.Android
      ? getAndroidCardProps()
      : getIosCardProps();
  };

  const getCardAction = () => {
    return type === MobileAppsSubmissionStatus.TypeEnum.Android
      ? getAndroidCardAction()
      : getIosCardAction();
  };

  const getAndroidCardAction = () => {
    const appType = type === MobileAppsSubmissionStatus.TypeEnum.Android ? 'android' : 'ios';
    switch (status) {
      case MobileAppsSubmissionStatus.UpdateStatusEnum.None:
        return (
          <Permissions allowed={[App.AppResourceSetEnum.EditSalesChannels]}>
            <div className={classes.actionContainer}>
              <Button
                disabled={isLoading}
                fullWidth
                fdKey={`submit-${type}-button`}
                onClick={() => handleFdReviewSubmit(type)}
              >
                <Translate id="Submit" />
              </Button>
            </div>
          </Permissions>
        );
      case MobileAppsSubmissionStatus.UpdateStatusEnum.Submitted:
      case MobileAppsSubmissionStatus.UpdateStatusEnum.InReview: {
        const secondStepClass =
          status === MobileAppsSubmissionStatus.UpdateStatusEnum.Submitted
            ? classes.incompleteStep
            : classes.completeStep;
        return (
          <>
            <div className={classes.reviewProcessContainer}>
              <SuccessOutlineIcon className={classes.completeStep} />
              <DashedLine className={classes.completeStep} />
              <SuccessOutlineIcon className={secondStepClass} />
              <DashedLine className={secondStepClass} />
              <SuccessOutlineIcon className={classes.incompleteStep} />
            </div>
            {isFlipdishStaff && (
              <InternalReviewControls
                onPass={() =>
                  handleInternalReviewPass(UpdateMobileAppsSubmissionStatus.TypeEnum.Android)
                }
                onReject={() =>
                  handleInternalReviewReject(UpdateMobileAppsSubmissionStatus.TypeEnum.Android)
                }
              />
            )}
          </>
        );
      }
      case MobileAppsSubmissionStatus.UpdateStatusEnum.Approved:
        return (
          <Button className={classes.successButton} fullWidth fdKey={`publish-${type}-button`}>
            <Translate id="Publish" />
          </Button>
        );
      case MobileAppsSubmissionStatus.UpdateStatusEnum.Rejected:
        return (
          <Button
            className={classes.checkSubmissionButton}
            fullWidth
            fdKey={`check-submission-${type}-button`}
          >
            <Translate id="Check_Submission" />
          </Button>
        );
      default:
        return (
          <div className={classes.actionContainer}>
            <div className={classes.publishedContainer}>
              <Typography className={classes.published} variant="body1">
                <Translate id="Published" />
              </Typography>
              <SuccessOutlineIcon className={classes.successIcon} />
            </div>
            <LinkIconButton
              fdKey={`navigate-to-${type}-details-icon-button`}
              size="small"
              to={`${location.pathname}/${appType}`}
            >
              <ArrowForwardIosOutlinedIcon />
            </LinkIconButton>
          </div>
        );
    }
  };

  const getIosCardAction = () => {
    return (
      <div className={classes.actionContainer}>
        <LinkButton
          fdKey="ios-get-in-touch-button"
          fullWidth
          color="primary"
          variant="contained"
          to="#"
          onClick={(e) => {
            window.location.href = `mailto:help@flipdish.com?subject=Request for iOS App`;
            e.preventDefault();
          }}
        >
          <Translate id="Get_in_touch" />
        </LinkButton>
      </div>
    );
  };

  return (
    <DescriptiveCard isLoading={loading} title={titleTranslationId} {...getCardProps()}>
      {getCardAction()}
    </DescriptiveCard>
  );
};
